import { MouseEvent, useState } from "react"
import { faArrowProgress, faLock, faLockOpen, faPrint, faReceipt, faTrash } from "@fortawesome/pro-light-svg-icons"

import { useApiMutation } from "../../../../../../../core/api/useApiMutation"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import {
    ActionBarItem,
    ActionBarMenu,
    ActionBarMenuItem,
    FloatingActionBar,
} from "../../../../../../../shared/components/table/FloatingActionBar"
import { UserMeetingsReceipt } from "../../../../../../nonAdminUser/reports/MeetingsReceipt/UserMeetingsReceipt"
import {
    batchUpdateDeliveryStatus,
    changeLockStatusMerchant,
    MerchantMeetingServiceOrdersQuery,
} from "../../meetingServiceApi"
import { FilteredStatuses, MeetingServiceOrder, MeetingServiceOrdersView } from "../../meetingServiceTypes"
import { getHiddenOrderStatuses } from "../../meetingServiceLib"
import { StatusButton } from "../../components/StatusMenu"
import { OrderPackageSlip } from "../../components/OrderPackageSlip"

const PRODUCTION_HIDDEN_STATUSES = getHiddenOrderStatuses(MeetingServiceOrdersView.PRODUCTION)

type Props = Readonly<{
    selectedOrders: MeetingServiceOrder[]
    selectedOrderIds: number[]
    merchantId: number
    onClearSelection: () => void
    onRequestDelete: (orders: MeetingServiceOrder[]) => void
}>

export function ProductionBulkActionBar({
    selectedOrders,
    selectedOrderIds,
    merchantId,
    onClearSelection,
    onRequestDelete,
}: Props) {
    const { l10n } = useAppLocalization()

    const [showBulkSlip, setShowBulkSlip] = useState(false)
    const [showBulkReceipt, setShowBulkReceipt] = useState(false)
    const [bulkStatusAnchor, setBulkStatusAnchor] = useState<{ top: number; left: number } | null>(null)

    const { mutate: updateStatus } = useApiMutation(batchUpdateDeliveryStatus, {
        async onSuccess(_params, _data, context) {
            await context.queryClient.invalidateQueries({
                queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE],
            })
        },
    })

    const { mutate: updateLock } = useApiMutation(changeLockStatusMerchant, {
        async onSuccess(_params, _data, context) {
            await context.queryClient.invalidateQueries({
                queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE],
            })
        },
    })

    const handleStatusClick = (event: MouseEvent<HTMLElement>) => {
        const rect = event.currentTarget.getBoundingClientRect()
        setBulkStatusAnchor((prev) => (prev ? null : { top: rect.top, left: rect.right }))
    }

    const handleStatusChange = (status: FilteredStatuses) => {
        updateStatus({ updateDeliveryStatus: { status, orderIds: selectedOrderIds } })
        onClearSelection()
    }

    const handleLock = (isLocked: boolean) => {
        updateLock({ updateLockStatus: { isLocked, orderIds: selectedOrderIds } })
        onClearSelection()
    }

    return (
        <>
            {selectedOrderIds.length > 0 && (
                <FloatingActionBar
                    selectedItemsLength={selectedOrderIds.length}
                    handleCloseActionBar={onClearSelection}
                >
                    <LocalizedStrict id="meeting-service-table-floating-action-bar-status" attrs={{ title: true }}>
                        <ActionBarItem icon={faArrowProgress} title="Status" onClick={handleStatusClick} />
                    </LocalizedStrict>

                    <ActionBarMenu icon={faLock} title={l10n.getString("meeting-service-table-lock-column")}>
                        <LocalizedStrict id="meeting-service-table-floating-action-bar-lock" attrs={{ title: true }}>
                            <ActionBarMenuItem icon={faLock} title="Lock" onClick={() => handleLock(true)} />
                        </LocalizedStrict>
                        <LocalizedStrict id="meeting-service-table-floating-action-bar-unlock" attrs={{ title: true }}>
                            <ActionBarMenuItem icon={faLockOpen} title="Unlock" onClick={() => handleLock(false)} />
                        </LocalizedStrict>
                    </ActionBarMenu>

                    <ActionBarMenu icon={faPrint} title={l10n.getString("meeting-service-production-view-print-menu")}>
                        <ActionBarMenuItem
                            icon={faPrint}
                            title={l10n.getString("meeting-service-table-production-slip-button")}
                            onClick={() => setShowBulkSlip(true)}
                        />
                        <ActionBarMenuItem
                            icon={faReceipt}
                            title={l10n.getString("user-home-canteen-upcoming-orders-receipt-item")}
                            onClick={() => setShowBulkReceipt(true)}
                        />
                    </ActionBarMenu>

                    <LocalizedStrict id="meeting-service-table-floating-action-bar-delete" attrs={{ title: true }}>
                        <ActionBarItem icon={faTrash} title="Delete" onClick={() => onRequestDelete(selectedOrders)} />
                    </LocalizedStrict>
                </FloatingActionBar>
            )}

            <StatusButton
                hiddenStatuses={PRODUCTION_HIDDEN_STATUSES}
                anchorEl={bulkStatusAnchor}
                onStatusChange={handleStatusChange}
                handleClose={() => setBulkStatusAnchor(null)}
                placement="top"
            />

            {showBulkSlip && (
                <OrderPackageSlip
                    open
                    onClose={() => setShowBulkSlip(false)}
                    orders={selectedOrders}
                    orderIds={selectedOrderIds}
                    merchantId={merchantId}
                />
            )}

            {showBulkReceipt && (
                <UserMeetingsReceipt
                    open={showBulkReceipt}
                    onClose={() => setShowBulkReceipt(false)}
                    singleMode={false}
                    meetings={selectedOrders}
                />
            )}
        </>
    )
}
